import { useState, useEffect } from "react";
import { X, Upload, FileText, CreditCard, MapPin, Check, ShieldCheck, Loader2, Info } from "lucide-react";
import { uploadDocumentsDirect } from "@/lib/api";
import { RgpdProtectionNotice } from "@/components/AidesPACWizard";

const DOC_CONFIG = [
  { key: "avis_imposition", label: "Avis d'imposition 2024 ou 2025", icon: FileText, hint: "PDF ou photo lisible" },
  { key: "cni_recto_mr", label: "CNI Mr — recto", icon: CreditCard, hint: "Recto de la carte d'identité de Monsieur" },
  { key: "cni_verso_mr", label: "CNI Mr — verso", icon: CreditCard, hint: "Verso de la carte d'identité de Monsieur" },
  { key: "cni_recto_mme", label: "CNI Mme — recto", icon: CreditCard, hint: "Recto de la carte d'identité de Madame" },
  { key: "cni_verso_mme", label: "CNI Mme — verso", icon: CreditCard, hint: "Verso de la carte d'identité de Madame" },
  { key: "justif_domicile", label: "Justificatif de domicile", icon: MapPin, hint: "Facture énergie / eau < 3 mois (pas de facture mobile)" },
];

export default function DocumentsOnlyModal({ open, onClose }) {
  const [identity, setIdentity] = useState({ first_name: "", last_name: "", email: "", phone: "", counselor_reference: "" });
  const [composition, setComposition] = useState("couple");
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setIdentity({ first_name: "", last_name: "", email: "", phone: "", counselor_reference: "" });
        setComposition("couple"); setFiles({}); setDone(false); setError("");
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const docs = composition === "single"
    ? DOC_CONFIG.filter((d) => !d.key.endsWith("_mme"))
    : DOC_CONFIG;
  const selectedCount = Object.values(files).filter(Boolean).length;

  const onFile = (key, file) => {
    if (!file) { setFiles((f) => { const c = { ...f }; delete c[key]; return c; }); return; }
    if (file.size > 5 * 1024 * 1024) { setError(`Le fichier "${file.name}" dépasse 5 MB.`); return; }
    const ok = /(pdf|jpe?g|png|webp|heic|heif)$/i.test(file.name) || /^(application\/pdf|image\/)/.test(file.type);
    if (!ok) { setError(`Format non supporté (PDF, JPG, PNG) pour "${file.name}".`); return; }
    setError("");
    setFiles((f) => ({ ...f, [key]: file }));
  };

  const canSubmit = identity.first_name.trim() && identity.last_name.trim()
    && /^\S+@\S+\.\S+$/.test(identity.email) && selectedCount > 0;

  const submit = async () => {
    if (!canSubmit) { setError("Merci de renseigner votre nom, email et au moins un document."); return; }
    setUploading(true); setError("");
    try {
      await uploadDocumentsDirect({
        first_name: identity.first_name.trim(),
        last_name: identity.last_name.trim(),
        email: identity.email.trim(),
        phone: identity.phone.trim(),
        counselor_reference: identity.counselor_reference.trim(),
        files,
      });
      setDone(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Envoi échoué. Réessayez.";
      setError(typeof msg === "string" ? msg : "Envoi échoué.");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="documents-only-modal"
      className="fixed inset-0 z-[9999] flex flex-col bg-[#0F172A]"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-10 py-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#10B981] to-[#059669] grid place-items-center">
            <Upload className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-white text-sm font-semibold tracking-tight">Déposer mes documents</div>
            <div className="text-white/50 text-[11px] uppercase tracking-[0.22em]">ECONATION × Rénobat</div>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="documents-only-close"
          aria-label="Fermer"
          className="h-10 w-10 rounded-full grid place-items-center bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 hover:text-white transition"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-5 sm:px-10 py-10">
          {done ? (
            <div data-testid="documents-only-success" className="text-center animate-[fadein_.6s_ease-out]">
              <div className="mx-auto mb-8 h-20 w-20 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] grid place-items-center shadow-[0_20px_60px_-10px_rgba(16,185,129,0.4)]">
                <Check className="h-9 w-9 text-white" strokeWidth={3} />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-6">
                Documents reçus{identity.first_name ? `, ${identity.first_name}` : ""} !
              </h2>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-lg mx-auto mb-4">
                Votre conseiller ECONATION va traiter vos pièces et vous recontacter <strong className="text-[#0055A4]">sous 24h ouvrées</strong>.
              </p>
              <p className="text-white/50 text-sm max-w-lg mx-auto mb-10">
                Un email de confirmation a été envoyé à <strong className="text-white/80">{identity.email}</strong>.
              </p>
              <button
                onClick={onClose}
                data-testid="documents-only-success-close"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 px-8 py-3.5 text-white text-sm font-semibold tracking-wide transition"
              >
                Retour au site
              </button>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/25 bg-[#10B981]/[0.06] px-3 py-1.5 mb-6">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#10B981] font-bold">Dépôt sécurisé</span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-4">
                Transmettez vos pièces en toute sécurité.
              </h2>
              <p className="flex items-start gap-2 text-white/60 text-sm leading-relaxed mb-8">
                <Info className="h-4 w-4 mt-0.5 text-[#10B981] flex-shrink-0" />
                <span>Vous avez déjà été en contact avec un conseiller ECONATION ? Déposez ici vos pièces justificatives directement.</span>
              </p>

              {/* Identité rapide */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 mb-6">
                <div className="text-white/85 font-semibold text-sm mb-4">Votre identité</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    data-testid="doc-first-name"
                    placeholder="Prénom"
                    value={identity.first_name}
                    onChange={(e) => setIdentity({ ...identity, first_name: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-[#10B981] rounded-xl px-4 py-3 text-base text-white placeholder:text-white/30 outline-none"
                  />
                  <input
                    data-testid="doc-last-name"
                    placeholder="Nom"
                    value={identity.last_name}
                    onChange={(e) => setIdentity({ ...identity, last_name: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-[#10B981] rounded-xl px-4 py-3 text-base text-white placeholder:text-white/30 outline-none"
                  />
                  <input
                    data-testid="doc-email"
                    type="email" inputMode="email"
                    placeholder="Email"
                    value={identity.email}
                    onChange={(e) => setIdentity({ ...identity, email: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-[#10B981] rounded-xl px-4 py-3 text-base text-white placeholder:text-white/30 outline-none"
                  />
                  <input
                    data-testid="doc-phone"
                    type="tel" inputMode="tel"
                    placeholder="Téléphone (facultatif)"
                    value={identity.phone}
                    onChange={(e) => setIdentity({ ...identity, phone: e.target.value })}
                    className="w-full bg-white/[0.03] border border-white/10 focus:border-[#10B981] rounded-xl px-4 py-3 text-base text-white placeholder:text-white/30 outline-none"
                  />
                  <input
                    data-testid="doc-counselor-ref"
                    placeholder="Nom de votre conseiller (facultatif)"
                    value={identity.counselor_reference}
                    onChange={(e) => setIdentity({ ...identity, counselor_reference: e.target.value })}
                    className="sm:col-span-2 w-full bg-white/[0.03] border border-white/10 focus:border-[#10B981] rounded-xl px-4 py-3 text-base text-white placeholder:text-white/30 outline-none"
                  />
                </div>
              </div>

              {/* Composition du foyer */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 mb-6">
                <div className="text-white/85 font-semibold text-sm mb-4">Composition du foyer</div>
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { v: "single", l: "Célibataire" },
                    { v: "couple", l: "Couple" },
                    { v: "family", l: "Famille" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      data-testid={`doc-only-composition-${opt.v}`}
                      onClick={() => { setComposition(opt.v); setError(""); }}
                      className={`rounded-xl border-2 py-2.5 px-3 text-sm font-semibold transition ${
                        composition === opt.v
                          ? "border-[#10B981] bg-[#10B981]/10 text-white"
                          : "border-white/10 bg-white/[0.02] text-white/75 hover:border-white/25"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
                <div className="mt-3 text-white/50 text-[11.5px] leading-relaxed">
                  {composition === "single"
                    ? "Nous vous demanderons uniquement votre CNI (recto + verso)."
                    : "Nous vous demanderons la CNI de Monsieur et Madame (recto + verso pour chacun)."}
                </div>
              </div>

              {/* Fichiers */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 mb-6">
                <div className="text-white/85 font-semibold text-sm mb-4">Vos documents</div>
                <div className="space-y-2.5">
                  {docs.map((d) => {
                    const Ic = d.icon;
                    const selected = files[d.key];
                    return (
                      <label
                        key={d.key}
                        data-testid={`doc-only-${d.key}`}
                        className={`flex items-center gap-3 rounded-xl border-2 border-dashed p-3.5 cursor-pointer transition ${
                          selected ? "border-[#10B981]/50 bg-[#10B981]/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/25"
                        }`}
                      >
                        <div className={`h-9 w-9 rounded-lg grid place-items-center flex-shrink-0 ${selected ? "bg-[#10B981]/20 text-[#10B981]" : "bg-white/[0.05] text-white/50"}`}>
                          {selected ? <Check className="h-4 w-4" strokeWidth={3} /> : <Ic className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">{d.label}</div>
                          <div className="text-white/50 text-[11px] truncate">
                            {selected ? `${selected.name} · ${(selected.size/1024).toFixed(0)} KB` : d.hint}
                          </div>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
                          className="hidden"
                          data-testid={`doc-only-input-${d.key}`}
                          onChange={(e) => onFile(d.key, e.target.files?.[0] || null)}
                        />
                        <div className="text-white/40 text-xs uppercase tracking-wide flex-shrink-0">
                          {selected ? "Remplacer" : "Choisir"}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {error && <div className="text-[#FCA5A5] text-sm mb-4" data-testid="doc-only-error">{error}</div>}

              <RgpdProtectionNotice />

              <button
                onClick={submit}
                disabled={uploading || !canSubmit}
                data-testid="doc-only-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#10B981] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-4 text-white text-sm font-semibold tracking-wide transition shadow-lg shadow-[#10B981]/30"
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours...</>
                ) : (
                  <><Upload className="h-4 w-4" /> Envoyer {selectedCount > 0 ? `${selectedCount} document(s)` : "les documents"}</>
                )}
              </button>

              <div className="mt-4 flex items-center gap-2 text-white/40 text-[11px]">
                <ShieldCheck className="h-3.5 w-3.5 text-[#10B981]" />
                <span>Transfert chiffré HTTPS · 5 MB max par fichier · PDF, JPG, PNG</span>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
